import { SERIES_COLORS } from "@/lib/chartPalettes";
import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import { useDebounceFn } from "@vueuse/core";
import useMyFetch from "@stores/useMyFetch";
import { useSurveysStore } from "@stores/surveys";
import type {
  PivotColumn,
  PivotDims,
  PivotFilterOptions,
  PivotGroupCell,
  PivotGroupsPayload,
  PivotMetrics,
  PivotMetricsPayload,
  PivotRespondentGroup,
  Quota,
  SurveyLight,
  Target,
} from "@/types";
import { DEFAULT_ROW_DIMENSIONS, defaultDimensionPicks, type DimensionPick } from "@views/Admin/CampaignsPivot/pivotDimensions";
import { DEFAULT_METRICS } from "@views/Admin/CampaignsPivot/pivotMetrics";

export type PivotMode = "metrics" | "groups";

// How the metrics read: every metric in one table, one table per metric, or one line chart per metric.
export type MetricsView = "combined" | "split" | "chart";

export interface SelectedSurvey {
  id: number;
  token: string | null;
  name: string | null;
  account_id: number | null;
}

export interface MetricsTableRow {
  key: string;
  dims: PivotDims;
  metrics: Partial<PivotMetrics>;
  cells: Record<string, Partial<PivotMetrics>>;
  isSpecial?: "unattributed" | "total";
}

export interface MetricsTableData {
  rows: MetricsTableRow[];
  columns: PivotColumn[];
  rowDimensions: string[];
  columnDimensions: string[];
  multiSurvey: boolean;
}

export interface GroupsTableRow {
  key: string;
  dims: PivotDims;
  cost: number | null;
  groups: Record<string, PivotGroupCell>;
  isSpecial?: "unattributed" | "total";
}

export interface GroupsTableData {
  rows: GroupsTableRow[];
  col_headers: Array<{ type: "group"; id: string; label: string; color: string | null; target_id: number | null }>;
  rowDimensions: string[];
}

export interface PivotState {
  accountIds?: number[];
  surveyIds?: number[];
  surveyTokenMap?: Record<number, string>;
  rowDimensions?: DimensionPick[] | null;
  columnDimensions?: DimensionPick[] | null;
  rowFilters?: Record<string, string[]>;
  dateFrom?: string | null;
  dateTo?: string | null;
  pivotMode?: PivotMode;
  metricsView?: MetricsView;
  selectedMetrics?: string[] | null;
  respondentGroups?: PivotRespondentGroup[];
}

const ALL_GROUP: PivotRespondentGroup = { id: "__all__", name: "All", color: null };

export const useCampaignsPivotStore = defineStore("campaignsPivot", () => {
  const surveysStore = useSurveysStore();

  // ── Scope: the accounts set the pool, the surveys narrow inside it ────────
  const selectedAccountIds = ref<number[]>([]);
  const selectedSurveyIds = ref<number[]>([]);
  const selectedSurveyMap = ref<Record<number, SelectedSurvey>>({});

  const selectedSurveyId = computed(() => selectedSurveyIds.value[0] ?? null);
  const selectedSurveys = computed(() => selectedSurveyIds.value.map((id) => selectedSurveyMap.value[id]).filter(Boolean));

  // ── Survey metadata (from the first selected survey) ──────────────────────
  const surveyQuestions = ref<Record<string, unknown>[]>([]);
  const surveyTargets = ref<Array<Target & { quotas?: Quota[] }>>([]);
  const filterOptions = ref<PivotFilterOptions>({});
  const metadataLoading = ref(false);

  // ── Dimensions + filters ──────────────────────────────────────────────────
  const rowDimensions = ref<DimensionPick[]>(defaultDimensionPicks(DEFAULT_ROW_DIMENSIONS));
  const columnDimensions = ref<DimensionPick[]>(defaultDimensionPicks());
  const rowFilters = ref<Record<string, string[]>>({});

  // Without survey nor account, a MCampaign status filter is scope enough:
  // the server then takes every survey with a campaign in that status.
  const statusScope = computed(() => rowFilters.value.m_status ?? []);
  const hasScope = computed(
    () => selectedSurveyIds.value.length > 0 || selectedAccountIds.value.length > 0 || statusScope.value.length > 0,
  );

  // ── Respondent groups ─────────────────────────────────────────────────────
  const respondentGroups = ref<PivotRespondentGroup[]>([]);

  // ── Period ────────────────────────────────────────────────────────────────
  const dateFrom = ref<string | null>(null);
  const dateTo = ref<string | null>(null);

  // ── Pivot mode ────────────────────────────────────────────────────────────
  const pivotMode = ref<PivotMode>("metrics");
  const metricsView = ref<MetricsView>("combined");

  const metricsPayload = ref<PivotMetricsPayload | null>(null);
  const groupsPayload = ref<PivotGroupsPayload | null>(null);

  const selectedMetrics = ref<string[]>([...DEFAULT_METRICS]);

  const metricsLoading = ref(false);
  const groupsLoading = ref(false);
  const error = ref<string | null>(null);

  // ── Computed ──────────────────────────────────────────────────────────────

  const activeRowDimensions = computed(() => rowDimensions.value.filter((d) => d.checked).map((d) => d.value));
  const activeColumnDimensions = computed(() => columnDimensions.value.filter((d) => d.checked).map((d) => d.value));

  const loading = computed(() => metricsLoading.value || groupsLoading.value);

  const allGroups = computed(() => [ALL_GROUP, ...respondentGroups.value]);

  const pivotData = computed<GroupsTableData | null>(() => {
    const payload = groupsPayload.value;
    if (!payload) return null;

    const groups = allGroups.value;
    const rows: GroupsTableRow[] = payload.rows.map((row, i) => ({ key: `row_${i}`, dims: row.dims, cost: row.cost, groups: row.groups }));

    const unattributed = payload.unattributed || {};
    if (Object.values(unattributed).some((g) => g.n > 0)) {
      const unattribGroups: Record<string, PivotGroupCell> = {};
      for (const g of groups) unattribGroups[g.id] = { n: unattributed[g.id]?.n || 0, cpi: null };
      rows.push({ key: "__unattributed__", dims: {}, isSpecial: "unattributed", cost: null, groups: unattribGroups });
    }

    rows.push({ key: "__total__", dims: {}, isSpecial: "total", cost: payload.total.cost, groups: payload.total.groups });

    return {
      rows,
      col_headers: groups.map((g) => ({ type: "group", id: g.id, label: g.name, color: g.color ?? null, target_id: g.target_id ?? null })),
      rowDimensions: activeRowDimensions.value,
    };
  });

  const metricsData = computed<MetricsTableData | null>(() => {
    const payload = metricsPayload.value;
    if (!payload) return null;

    const rows: MetricsTableRow[] = payload.rows.map((row, i) => ({ key: `row_${i}`, dims: row.dims, metrics: row.metrics, cells: row.cells }));

    if (payload.unattributed_n > 0) {
      rows.push({
        key: "__unattributed__", dims: {}, isSpecial: "unattributed", cells: {},
        metrics: { n: payload.unattributed_n, raw_completes_valid: payload.unattributed_n },
      });
    }

    rows.push({ key: "__total__", dims: {}, isSpecial: "total", metrics: payload.total, cells: payload.total_cells ?? {} });

    return {
      rows,
      columns: payload.columns ?? [],
      rowDimensions: activeRowDimensions.value,
      columnDimensions: activeColumnDimensions.value,
      multiSurvey: payload.total.surveys > 1,
    };
  });

  // ── Requests ──────────────────────────────────────────────────────────────

  const surveyTokens = () => selectedSurveyIds.value.map((id) => selectedSurveyMap.value[id]?.token).filter(Boolean);

  function scopeParams() {
    const params = new URLSearchParams();
    for (const token of surveyTokens()) params.append("survey_tokens[]", token as string);
    for (const id of selectedAccountIds.value) params.append("account_ids[]", String(id));
    if (!selectedSurveyIds.value.length && !selectedAccountIds.value.length) {
      for (const status of statusScope.value) params.append("m_status[]", status);
    }
    return params;
  }

  function pivotBody(mode: PivotMode) {
    return {
      pivot: {
        mode,
        survey_tokens: surveyTokens(),
        account_ids: selectedAccountIds.value,
        group_by: activeRowDimensions.value,
        column_by: mode === "metrics" ? activeColumnDimensions.value : [],
        filters: rowFilters.value,
        date_from: dateFrom.value || null,
        date_to: dateTo.value || null,
        ...(mode === "groups" && {
          respondent_groups: allGroups.value.map((g) => ({
            id: g.id,
            target_id: g.target_id || null,
            conditions: g.conditions || [],
          })),
        }),
      },
    };
  }

  async function fetchMetricsRows() {
    if (!hasScope.value) {
      metricsPayload.value = null;
      return;
    }
    metricsLoading.value = true;
    error.value = null;
    try {
      const { response, data } = await useMyFetch("/pivot_analytics/data").json<PivotMetricsPayload & { error?: string }>().post(pivotBody("metrics"));
      if (response.value?.ok && data.value) metricsPayload.value = data.value;
      else error.value = data.value?.error || "Failed to load metrics data";
    } finally {
      metricsLoading.value = false;
    }
  }

  async function fetchGroupsRows() {
    if (!hasScope.value) {
      groupsPayload.value = null;
      return;
    }
    groupsLoading.value = true;
    error.value = null;
    try {
      const { response, data } = await useMyFetch("/pivot_analytics/data").json<PivotGroupsPayload & { error?: string }>().post(pivotBody("groups"));
      if (response.value?.ok && data.value) groupsPayload.value = data.value;
      else error.value = data.value?.error || "Failed to load groups data";
    } finally {
      groupsLoading.value = false;
    }
  }

  async function fetchRows() {
    await Promise.all([fetchMetricsRows(), fetchGroupsRows()]);
  }

  async function fetchFilterOptions() {
    if (!hasScope.value) {
      filterOptions.value = {};
      return;
    }
    const { response, data } = await useMyFetch(`/pivot_analytics/filter_options?${scopeParams()}`).json<PivotFilterOptions>();
    if (response.value?.ok && data.value) filterOptions.value = data.value;
  }

  async function fetchSurveyMetadata() {
    const first = selectedSurveyId.value ? selectedSurveyMap.value[selectedSurveyId.value] : null;
    if (!first?.token) {
      surveyQuestions.value = [];
      surveyTargets.value = [];
      return;
    }
    metadataLoading.value = true;
    try {
      const [questionsRes, targetsRes, quotasRes] = await Promise.all([
        useMyFetch(`/surveys/${first.token}/questions`).json<Record<string, unknown>[]>(),
        useMyFetch(`/survey_targets/survey_index?survey_token=${first.token}`).json<Target[]>(),
        useMyFetch(`/surveys/${first.token}/quotas`).json<{ quotas: Quota[] }>(),
      ]);
      if (questionsRes.response.value?.ok && questionsRes.data.value) surveyQuestions.value = questionsRes.data.value;
      if (targetsRes.response.value?.ok && targetsRes.data.value) surveyTargets.value = targetsRes.data.value;
      if (targetsRes.response.value?.ok && quotasRes.response.value?.ok && quotasRes.data.value) {
        const quotasByTargetId: Record<number, Quota[]> = {};
        for (const quota of quotasRes.data.value.quotas) {
          if (quota.survey_target_id != null) (quotasByTargetId[quota.survey_target_id] ??= []).push(quota);
        }
        surveyTargets.value = surveyTargets.value.map((t) => ({ ...t, quotas: quotasByTargetId[t.id] ?? [] }));
      }
    } finally {
      metadataLoading.value = false;
    }
  }

  // ── Scope changes ─────────────────────────────────────────────────────────

  function toSelectedSurvey(survey: SurveyLight): SelectedSurvey {
    return { id: survey.id, token: survey.token, name: survey.name, account_id: survey.account_id ?? null };
  }

  async function toggleSurvey(surveyId: number) {
    error.value = null;
    if (selectedSurveyIds.value.includes(surveyId)) {
      selectedSurveyIds.value = selectedSurveyIds.value.filter((id) => id !== surveyId);
    } else {
      const option = (surveysStore.lightSurveys as SurveyLight[]).find((s) => s.id === surveyId);
      if (!option) return;
      selectedSurveyMap.value = { ...selectedSurveyMap.value, [surveyId]: toSelectedSurvey(option) };
      selectedSurveyIds.value = [...selectedSurveyIds.value, surveyId];
    }
    await onScopeChanged();
  }

  async function setAccounts(accountIds: number[]) {
    error.value = null;
    selectedAccountIds.value = accountIds;
    // A survey that just left the pool is dropped from the selection.
    if (accountIds.length) {
      const pool = new Set(accountIds);
      selectedSurveyIds.value = selectedSurveyIds.value.filter((id) => {
        const accountId = selectedSurveyMap.value[id]?.account_id;
        return accountId == null || pool.has(accountId);
      });
    }
    await onScopeChanged();
  }

  async function onScopeChanged() {
    if (!hasScope.value) {
      surveyQuestions.value = [];
      surveyTargets.value = [];
      filterOptions.value = {};
      respondentGroups.value = [];
      metricsPayload.value = null;
      groupsPayload.value = null;
      return;
    }
    await Promise.all([fetchFilterOptions(), fetchSurveyMetadata()]);
  }

  async function applyState(state: PivotState) {
    rowDimensions.value = state.rowDimensions || defaultDimensionPicks(DEFAULT_ROW_DIMENSIONS);
    columnDimensions.value = state.columnDimensions || defaultDimensionPicks();
    rowFilters.value = state.rowFilters || {};
    dateFrom.value = state.dateFrom || null;
    dateTo.value = state.dateTo || null;
    pivotMode.value = state.pivotMode || "metrics";
    metricsView.value = state.metricsView || "combined";
    if (state.selectedMetrics) selectedMetrics.value = state.selectedMetrics;
    respondentGroups.value = state.respondentGroups || [];
    metricsPayload.value = null;
    groupsPayload.value = null;
    error.value = null;

    const ids = state.surveyIds || [];
    const tokMap = state.surveyTokenMap || {};
    selectedSurveyMap.value = {};
    for (const id of ids) {
      const option = (surveysStore.lightSurveys as SurveyLight[]).find((s) => s.id === id);
      selectedSurveyMap.value[id] = option
        ? toSelectedSurvey(option)
        : { id, token: tokMap[id] || null, name: null, account_id: null };
    }
    selectedSurveyIds.value = ids;
    selectedAccountIds.value = state.accountIds || [];

    if (!hasScope.value) return;
    await Promise.all([fetchSurveyMetadata(), fetchFilterOptions()]);
  }

  const refetchRows = useDebounceFn(fetchRows, 400);
  const refetchGroupsRows = useDebounceFn(fetchGroupsRows, 400);

  watch(
    [selectedSurveyIds, selectedAccountIds, dateFrom, dateTo, activeRowDimensions, activeColumnDimensions, rowFilters],
    () => refetchRows(),
    { deep: true },
  );

  watch(respondentGroups, () => refetchGroupsRows(), { deep: true });

  // The status filter is the scope when nothing else is: the option lists follow it.
  watch(statusScope, () => {
    if (!selectedSurveyIds.value.length && !selectedAccountIds.value.length) fetchFilterOptions();
  });

  // ── Dimension pickers: a dimension groups rows or columns, never both ──────

  function setDimensionChecked(list: "row" | "column", value: string, checked: boolean) {
    const [own, twin] = list === "row" ? [rowDimensions, columnDimensions] : [columnDimensions, rowDimensions];
    own.value = own.value.map((d) => (d.value === value ? { ...d, checked } : d));
    if (checked) twin.value = twin.value.map((d) => (d.value === value ? { ...d, checked: false } : d));
  }

  // ── Group management ──────────────────────────────────────────────────────

  function addGroup(group: Omit<PivotRespondentGroup, "id" | "color">) {
    respondentGroups.value.push({ id: `g_${Date.now()}`, color: nextGroupColor(), ...group });
  }

  function removeGroup(groupId: string) {
    respondentGroups.value = respondentGroups.value.filter((g) => g.id !== groupId);
  }

  function updateGroup(groupId: string, updates: Partial<PivotRespondentGroup>) {
    const idx = respondentGroups.value.findIndex((g) => g.id === groupId);
    if (idx !== -1) respondentGroups.value[idx] = { ...respondentGroups.value[idx], ...updates };
  }

  function nextGroupColor() {
    return SERIES_COLORS[respondentGroups.value.length % SERIES_COLORS.length];
  }

  return {
    // state
    selectedAccountIds, selectedSurveyIds, selectedSurveyMap, selectedSurveys, selectedSurveyId, hasScope,
    surveyQuestions, surveyTargets, filterOptions, metadataLoading,
    rowDimensions, columnDimensions, activeRowDimensions, activeColumnDimensions, rowFilters,
    respondentGroups, dateFrom, dateTo,
    pivotData, metricsData,
    pivotMode, metricsView, selectedMetrics,
    loading, metricsLoading, error,
    // actions
    toggleSurvey, setAccounts, applyState, setDimensionChecked,
    fetchSurveyMetadata, fetchFilterOptions, fetchRows,
    addGroup, removeGroup, updateGroup,
  };
});
